import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider } from '@/components/auth/auth-context';
import { ConfirmProvider } from '@/components/common/confirm';
import { I18nProvider } from '@/i18n';
import { ApiError } from '@/lib/api';

const client = new QueryClient({
  defaultOptions: {
    queries: {
      // Most lists are also pushed over the socket, so aggressive refetching
      // would duplicate work. Refetch on focus is kept because a laptop coming
      // out of sleep has usually missed frames.
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <QueryClientProvider client={client}>
      {/* Outermost of the UI providers: ConfirmProvider renders dialog copy and
          AuthProvider surfaces sign-in errors, so both need a translator. */}
      <I18nProvider>
        <AuthProvider>
          <TooltipProvider delayDuration={120}>
            <ConfirmProvider>
              {children}
              <Toaster
                position="bottom-right"
                closeButton
                // Matching the app's own surfaces rather than sonner's defaults;
                // a toast that looks like a different product is jarring.
                toastOptions={{
                  classNames: {
                    toast:
                      'rounded-md border border-line bg-surface text-ink shadow-pop text-sm gap-2',
                    description: 'text-ink-muted text-xs',
                    actionButton: 'bg-primary text-primary-ink rounded text-xs h-6 px-2',
                    cancelButton: 'bg-accent text-accent-ink rounded text-xs h-6 px-2',
                  },
                }}
              />
            </ConfirmProvider>
          </TooltipProvider>
        </AuthProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}
